(() => {
  const PRODUCT_INFO_TAG = 'product-info';
  const BOOT_LIVE_CART_SUPPRESSION_MS = 1200;
  const nativeCustomElementDefine = window.customElements.define.bind(window.customElements);

  function runForcedLiveCartCheck(instance) {
    instance.__forceLiveCartCheck = true;

    try {
      instance.checkLiveCartInfo();
    } finally {
      instance.__forceLiveCartCheck = false;
    }
  }

  function getProductGallerySlides(instance) {
    return Array.from(instance.querySelectorAll('[data-product-single-media-wrapper]'));
  }

  function getDormantSlideImage(slide) {
    return slide?.querySelector('img[data-pdp-lazy-image]');
  }

  function isTallDesktopLayout(instance) {
    const desktopBreakpoint = window.theme?.sizes?.large ?? 1024;
    return instance.getAttribute('data-tall-layout') === 'true' && window.innerWidth >= desktopBreakpoint;
  }

  function hydrateSlideImage(slide, { prioritize = false } = {}) {
    const image = getDormantSlideImage(slide);
    if (!image) return;

    if (prioritize) {
      image.loading = 'eager';
      image.fetchPriority = 'high';
    } else {
      const loading = image.getAttribute('data-loading');
      const fetchPriority = image.getAttribute('data-fetchpriority');

      if (loading) image.loading = loading;
      if (fetchPriority) image.fetchPriority = fetchPriority;
    }

    const sizes = image.getAttribute('data-sizes');
    const srcset = image.getAttribute('data-srcset');
    const src = image.getAttribute('data-src');

    if (sizes && !image.getAttribute('sizes')) {
      image.setAttribute('sizes', sizes);
    }

    if (srcset && image.getAttribute('srcset') !== srcset) {
      image.setAttribute('srcset', srcset);
    }

    if (src && image.getAttribute('src') !== src) {
      image.setAttribute('src', src);
    }

    image.removeAttribute('data-pdp-lazy-image');
  }

  function hydrateSlidesAroundSelection(instance, mediaId = null) {
    const slides = getProductGallerySlides(instance);
    if (!slides.length) return;

    const selectedSlide =
      (mediaId && instance.querySelector(`[data-product-single-media-wrapper][data-media-id="${mediaId}"]`)) ||
      instance.querySelector('[data-product-single-media-wrapper]:not(.media--hidden)') ||
      slides[0];

    if (!selectedSlide) return;

    hydrateSlideImage(selectedSlide, { prioritize: true });

    if (slides.length < 2) return;

    const selectedIndex = slides.indexOf(selectedSlide);
    if (selectedIndex === -1) return;

    const previousIndex = (selectedIndex - 1 + slides.length) % slides.length;
    const nextIndex = (selectedIndex + 1) % slides.length;

    hydrateSlideImage(slides[previousIndex]);
    hydrateSlideImage(slides[nextIndex]);
  }

  function hydrateAllSlideImages(instance, mediaId = null) {
    const slides = getProductGallerySlides(instance);
    if (!slides.length) return;

    const selectedSlide =
      (mediaId && instance.querySelector(`[data-product-single-media-wrapper][data-media-id="${mediaId}"]`)) ||
      instance.querySelector('[data-product-single-media-wrapper]:not(.media--hidden)') ||
      slides[0];

    slides.forEach((slide) => {
      hydrateSlideImage(slide, { prioritize: slide === selectedSlide });
    });
  }

  function patchProductInfo(ProductInfo) {
    if (!ProductInfo || ProductInfo.prototype.__pdpPerformancePatched) return;

    const originalConnectedCallback = ProductInfo.prototype.connectedCallback;
    const originalDisconnectedCallback = ProductInfo.prototype.disconnectedCallback;
    const originalCheckLiveCartInfo = ProductInfo.prototype.checkLiveCartInfo;
    const originalSwitchMedia = ProductInfo.prototype.switchMedia;

    ProductInfo.prototype.enableCartCloseSync = function enableCartCloseSync() {
      if (this.__cartCloseSyncEnabled || !this.checkLiveCartInfoCallback) return;

      document.addEventListener('theme:cart:close', this.checkLiveCartInfoCallback);
      this.__cartCloseSyncEnabled = true;
    };

    ProductInfo.prototype.startLiveCartBootSuppression = function startLiveCartBootSuppression() {
      window.clearTimeout(this.__liveCartBootTimerId);
      this.__suppressLiveCartChecks = true;
      this.__liveCartBootTimerId = window.setTimeout(() => {
        this.__suppressLiveCartChecks = false;
        this.__liveCartBootTimerId = null;
      }, BOOT_LIVE_CART_SUPPRESSION_MS);
    };

    ProductInfo.prototype.scheduleRecentlyViewedRecording = function scheduleRecentlyViewedRecording() {
      if (this.__recentlyViewedScheduled || !this.dataset.productHandle) return;

      this.__recentlyViewedScheduled = true;

      const record = () => {
        const recordRecentlyViewed = window.Shopify?.Products?.recordRecentlyViewed;
        if (typeof recordRecentlyViewed !== 'function') return;

        try {
          recordRecentlyViewed({ handle: this.dataset.productHandle });
        } catch (error) {
          console.warn('Recently viewed recording error:', error);
        }
      };

      if ('requestIdleCallback' in window) {
        this.__recentlyViewedIdleId = window.requestIdleCallback(record, { timeout: 3000 });
        return;
      }

      this.__recentlyViewedTimeoutId = window.setTimeout(record, 1500);
    };

    ProductInfo.prototype.checkLiveCartInfo = function checkLiveCartInfoPatched(...args) {
      if (this.__suppressLiveCartChecks && !this.__forceLiveCartCheck) return;

      return originalCheckLiveCartInfo.apply(this, args);
    };

    ProductInfo.prototype.hydrateVisibleProductMedia = function hydrateVisibleProductMedia(mediaId = null) {
      if (isTallDesktopLayout(this)) {
        hydrateAllSlideImages(this, mediaId);
        return;
      }

      hydrateSlidesAroundSelection(this, mediaId);
    };

    ProductInfo.prototype.switchMedia = function switchMediaPatched(mediaId, ...args) {
      this.hydrateVisibleProductMedia(mediaId);

      if (typeof originalSwitchMedia === 'function') {
        return originalSwitchMedia.call(this, mediaId, ...args);
      }
    };

    ProductInfo.prototype.connectedCallback = function connectedCallbackPatched() {
      const productsApi = window.Shopify?.Products;
      const originalRecordRecentlyViewed = productsApi?.recordRecentlyViewed;

      this.startLiveCartBootSuppression();

      if (typeof originalRecordRecentlyViewed === 'function') {
        productsApi.recordRecentlyViewed = () => {};
      }

      originalConnectedCallback.call(this);

      if (typeof originalRecordRecentlyViewed === 'function') {
        productsApi.recordRecentlyViewed = originalRecordRecentlyViewed;
      }

      if (this.checkLiveCartInfoCallback) {
        document.removeEventListener('theme:cart:close', this.checkLiveCartInfoCallback);
      }

      this.checkLiveCartInfoCallback = () => runForcedLiveCartCheck(this);
      this.handleCartAddedForSync = () => {
        this.enableCartCloseSync();
        runForcedLiveCartCheck(this);
      };

      document.addEventListener('theme:cart:added', this.handleCartAddedForSync);
      this.__pdpMediaResizeCallback = () => this.hydrateVisibleProductMedia();
      document.addEventListener('theme:resize:width', this.__pdpMediaResizeCallback);
      this.scheduleRecentlyViewedRecording();
      this.hydrateVisibleProductMedia();
    };

    ProductInfo.prototype.disconnectedCallback = function disconnectedCallbackPatched() {
      document.removeEventListener('theme:cart:close', this.checkLiveCartInfoCallback);

      if (this.handleCartAddedForSync) {
        document.removeEventListener('theme:cart:added', this.handleCartAddedForSync);
      }

      if (this.__pdpMediaResizeCallback) {
        document.removeEventListener('theme:resize:width', this.__pdpMediaResizeCallback);
      }

      if (this.__recentlyViewedIdleId && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(this.__recentlyViewedIdleId);
      }

      if (this.__recentlyViewedTimeoutId) {
        window.clearTimeout(this.__recentlyViewedTimeoutId);
      }

      if (this.__liveCartBootTimerId) {
        window.clearTimeout(this.__liveCartBootTimerId);
      }

      this.__cartCloseSyncEnabled = false;
      this.__forceLiveCartCheck = false;
      this.__suppressLiveCartChecks = false;
      this.__pdpMediaResizeCallback = null;

      if (typeof originalDisconnectedCallback === 'function') {
        originalDisconnectedCallback.call(this);
      }
    };

    ProductInfo.prototype.__pdpPerformancePatched = true;
  }

  function patchProductModel(ProductModel) {
    if (!ProductModel || ProductModel.__pdpPerformancePatched) return;

    const originalLoadShopifyXR = ProductModel.loadShopifyXR;
    if (typeof originalLoadShopifyXR !== 'function') return;

    ProductModel.loadShopifyXR = function loadShopifyXRPatched() {
      const hasModels = document.querySelector('[id^="ModelJson-"], [data-shopify-xr]');
      if (!hasModels) return;

      originalLoadShopifyXR.call(this);
    };

    ProductModel.__pdpPerformancePatched = true;
  }

  if (!window.customElements.__pdpPerformancePatched) {
    window.customElements.define = function definePatched(name, constructor, options) {
      if (name === PRODUCT_INFO_TAG) {
        patchProductInfo(constructor);
      }

      return nativeCustomElementDefine(name, constructor, options);
    };

    window.customElements.__pdpPerformancePatched = true;
  }

  const existingProductInfo = window.customElements.get(PRODUCT_INFO_TAG);
  if (existingProductInfo) {
    patchProductInfo(existingProductInfo);
  }

  if (!window.__pdpProductModelHooked) {
    let productModelValue = window.ProductModel;

    Object.defineProperty(window, 'ProductModel', {
      configurable: true,
      get() {
        return productModelValue;
      },
      set(value) {
        productModelValue = value;
        patchProductModel(productModelValue);
      }
    });

    patchProductModel(productModelValue);
    window.__pdpProductModelHooked = true;
  } else if (window.ProductModel) {
    patchProductModel(window.ProductModel);
  }
})();
